
import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { SavedChartState, ChartSetList } from '../types';

const DB_NAME = 'JazzChartDB-v1';
const DB_VERSION = 4; // v4: add pendingDeletes store for offline-delete tombstones
const CHARTS_STORE_NAME = 'charts';
const SETTINGS_STORE_NAME = 'settings';
const SETLISTS_STORE_NAME = 'setlists'; // Renamed from collections
const PENDING_DELETES_STORE_NAME = 'pendingDeletes';
const LAST_OPENED_KEY = 'lastOpenedChartTitle';

interface JazzChartDB extends DBSchema {
  [CHARTS_STORE_NAME]: {
    key: string;
    value: SavedChartState;
  };
  [SETTINGS_STORE_NAME]: {
    key: string;
    value: string;
  };
  [SETLISTS_STORE_NAME]: {
    key: string;
    value: ChartSetList[];
  };
  [PENDING_DELETES_STORE_NAME]: {
    key: string;      // songTitle
    value: string;    // songTitle (stored as value too for simplicity)
  };
}

let dbPromise: Promise<IDBPDatabase<JazzChartDB>> | null = null;

const getDb = (): Promise<IDBPDatabase<JazzChartDB>> => {
  if (!dbPromise) {
    dbPromise = openDB<JazzChartDB>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion, newVersion) {
        console.log(`Upgrading database from version ${oldVersion} to ${newVersion}`);
        
        // Create charts store if it doesn't exist
        if (!db.objectStoreNames.contains(CHARTS_STORE_NAME)) {
          console.log('Creating charts store');
          db.createObjectStore(CHARTS_STORE_NAME, { keyPath: 'songTitle' });
        }
        
        // Create settings store if it doesn't exist
        if (!db.objectStoreNames.contains(SETTINGS_STORE_NAME)) {
          console.log('Creating settings store');
          db.createObjectStore(SETTINGS_STORE_NAME);
        }
        
        // Create setlists store if it doesn't exist
        if (!db.objectStoreNames.contains(SETLISTS_STORE_NAME)) {
          console.log('Creating setlists store');
          db.createObjectStore(SETLISTS_STORE_NAME);
        }

        // v4: Create pendingDeletes store for offline-delete tombstones
        if (!db.objectStoreNames.contains(PENDING_DELETES_STORE_NAME)) {
          console.log('Creating pendingDeletes store');
          db.createObjectStore(PENDING_DELETES_STORE_NAME);
        }
      },
    });
  }
  return dbPromise;
};

export const saveChart = async (chart: SavedChartState): Promise<void> => {
  const db = await getDb();
  await db.put(CHARTS_STORE_NAME, chart);
};

export const getChart = async (title: string): Promise<SavedChartState | undefined> => {
  const db = await getDb();
  return db.get(CHARTS_STORE_NAME, title);
};

// FIX: Add getAllCharts function to retrieve all chart objects from IndexedDB.
export const getAllCharts = async (): Promise<SavedChartState[]> => {
  const db = await getDb();
  return db.getAll(CHARTS_STORE_NAME);
};

export const getAllChartTitles = async (): Promise<string[]> => {
  const db = await getDb();
  const keys = await db.getAllKeys(CHARTS_STORE_NAME);
  // keys are IDBValidKey[], which can be string.
  return (keys as string[]).sort();
};

export const deleteChart = async (title: string): Promise<void> => {
  const db = await getDb();
  await db.delete(CHARTS_STORE_NAME, title);
};

export const getLastOpenedChartTitle = async (): Promise<string | undefined> => {
  const db = await getDb();
  return db.get(SETTINGS_STORE_NAME, LAST_OPENED_KEY);
};

export const setLastOpenedChartTitle = async (title: string): Promise<void> => {
  const db = await getDb();
  await db.put(SETTINGS_STORE_NAME, title, LAST_OPENED_KEY);
};

// Set List Management Functions (formerly Collection Management)
const SETLISTS_KEY = 'user_setlists';

export const saveSetLists = async (setLists: ChartSetList[]): Promise<void> => {
  try {
    const db = await getDb();
    await db.put(SETLISTS_STORE_NAME, setLists, SETLISTS_KEY);
  } catch (error) {
    console.error('Failed to save set lists, resetting database connection:', error);
    // Reset connection and try again
    resetDbConnection();
    const db = await getDb();
    await db.put(SETLISTS_STORE_NAME, setLists, SETLISTS_KEY);
  }
};

export const getSetLists = async (): Promise<ChartSetList[]> => {
  try {
    const db = await getDb();
    const setLists = await db.get(SETLISTS_STORE_NAME, SETLISTS_KEY);
    return setLists || [];
  } catch (error) {
    console.error('Failed to get set lists, resetting database connection:', error);
    // Reset connection and try again
    resetDbConnection();
    const db = await getDb();
    const setLists = await db.get(SETLISTS_STORE_NAME, SETLISTS_KEY);
    return setLists || [];
  }
};

// ─── Pending Deletes (tombstones for offline chart deletions) ─────────────

export const addPendingDelete = async (title: string): Promise<void> => {
  const db = await getDb();
  await db.put(PENDING_DELETES_STORE_NAME, title, title);
};

export const getPendingDeletes = async (): Promise<string[]> => {
  const db = await getDb();
  return (await db.getAllKeys(PENDING_DELETES_STORE_NAME)) as string[];
};

export const removePendingDelete = async (title: string): Promise<void> => {
  const db = await getDb();
  await db.delete(PENDING_DELETES_STORE_NAME, title);
};

// ─── Debug functions ──────────────────────────────────────────────────────

// Debug function to reset database connection
export const resetDbConnection = (): void => {
  dbPromise = null;
};

// Debug function to completely clear the database
export const clearDatabase = async (): Promise<void> => {
  try {
    const db = await getDb();
    db.close();
    
    // Delete the entire database
    const deleteRequest = indexedDB.deleteDatabase(DB_NAME);
    return new Promise((resolve, reject) => {
      deleteRequest.onsuccess = () => {
        console.log('Database deleted successfully');
        resetDbConnection();
        resolve();
      };
      deleteRequest.onerror = () => {
        console.error('Failed to delete database');
        reject(deleteRequest.error);
      };
    });
  } catch (error) {
    console.error('Error clearing database:', error);
    throw error;
  }
};
